import type { PropsWithChildren } from 'react';
import { isValidElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Collapse } from 'react-collapse';
import { defineMessages, FormattedMessage } from 'react-intl';
import useMeasure from 'react-use/lib/useMeasure';
import { twMerge } from 'tailwind-merge';

import { Button } from '../../components/Button/Button';
import CustomGallery from '../../components/CustomGallery';
import type { EjsBlockData, EjsBlockImage } from '../../editor/editorjs/@types';
import type { MessageParsedEmbeds } from '../ThreadMessage/threadTypes';
import { EmbedsOverview } from './EmbedsOverview';

const theme = { collapse: 'duration-150', content: 'relative' };
const messages = defineMessages({
  readMore: { defaultMessage: 'Lire la suite', id: 'wyK7au' },
});

// Needed to avoid a TS error when using the editorjs types.
const isEjsBlockImage = (block: EjsBlockData): block is EjsBlockImage => block.type === 'image';

/**
 *
 * @param children
 * @param limit
 * @param tolerance
 * @param timeToDeploy
 * @constructor
 */
export const MessageLimiter = ({
  children,
  limit = 264,
  tolerance = 96,
}: // timeToDeploy = 2000, // Removed for now as not really user friendly
PropsWithChildren<{
  limit?: number;
  tolerance?: number;
  timeToDeploy?: number;
}>) => {
  const [showAll, setShowAll] = useState(false);
  // const timerRef = useRef<number>();
  const [fullContentHeight, setFullContentHeight] = useState(0);
  const [ref, { height }] = useMeasure<HTMLDivElement>();

  useEffect(() => {
    // Hack to keep track of total content height even if when remove images when collapsed
    if (height > fullContentHeight) {
      setFullContentHeight(height);
    }
  }, [height, fullContentHeight]);

  const messageEmbeds = isValidElement(children)
    ? (JSON.parse(children.props.embeds || null) as MessageParsedEmbeds)
    : null;

  const isImagesOnly = useMemo(
    () => messageEmbeds?.blocks?.every(isEjsBlockImage),
    [messageEmbeds?.blocks],
  );

  const embededImages = useMemo(
    () =>
      messageEmbeds?.blocks?.filter(isEjsBlockImage).map(imageBlock => imageBlock.data.file?.url),
    [messageEmbeds?.blocks],
  );

  const show = useCallback(() => {
    setShowAll(true);
  }, [setShowAll]);

  // const onMouseEnter = useCallback(() => {
  //   clearTimeout(timerRef.current);
  //   timerRef.current = window.setTimeout(() => setShowAll(true), timeToDeploy);
  // }, [timeToDeploy]);

  // const onMouseLeave = useCallback(() => {
  //   clearTimeout(timerRef.current);
  // }, [timerRef]);

  const isLimiting = fullContentHeight > limit && !showAll;

  return (
    <div>
      <div
        className={twMerge('MessageLimiter', isLimiting && 'limited')}
        // onMouseEnter={isLimiting ? onMouseEnter : undefined}
        // onMouseLeave={isLimiting ? onMouseLeave : undefined}
      >
        <Collapse theme={theme} isOpened>
          <div style={showAll ? undefined : { maxHeight: limit }} className="overflow-hidden">
            <div ref={ref}>{children}</div>
          </div>
          {isLimiting && (
            <div
              className={twMerge(
                'messageCollapser flex flex-col w-full bg-gradient-to-t from-white via-white to-transparentWhite bottom-0 left-0 justify-end',
                !isImagesOnly && 'absolute pt-8',
              )}
              {...(!isImagesOnly && { style: { height: tolerance } })}
            >
              <Button
                className={twMerge('w-full text-center no-underline', !isImagesOnly && 'pt-8')}
                variant="link"
                onClick={show}
              >
                <div className="flex flex-col items-center justify-center py-2 px-3 rounded-full no-underline">
                  <span className="text-xxs uppercase tracking-[2.2px] text-gray-semiDark leading-4">
                    <FormattedMessage {...messages.readMore} />
                  </span>
                  <div className="transform -rotate-90 inline-block text-gray-medium w-4 h-4">
                    <i className="icon-left-chevron" style={{ fontSize: 8 }} />
                  </div>
                </div>
              </Button>
            </div>
          )}
        </Collapse>
      </div>
      {isLimiting && !!embededImages?.length && <CustomGallery photos={embededImages} />}
      {isLimiting && messageEmbeds && <EmbedsOverview messageEmbeds={messageEmbeds} />}
      <style>{`
        .MessageLimiter.limited .EditorImage {
          display: none;
        }
      `}</style>
    </div>
  );
};
